<template>
  <ac-modal v-model="isOpen">
    <div class="asPushPrompt">
      <span class="asPushPrompt_title" v-html="title" />
      <span class="asPushPrompt_text" v-html="text" />
      <div class="asPushPrompt_actions">
        <ac-button
          class="asPushPrompt_btn -accept"
          type="button"
          variant="tonal"
          block
          :aria-label="$t('yes')"
          @click="enablePush"
        >
          {{ $t('Subscribe') }}
        </ac-button>
        <ac-button
          class="asPushPrompt_btn -decline"
          type="button"
          variant="tonal"
          block
          :aria-label="$t('NO')"
          @click="declinePush"
        >
          {{ $t('Cancel') }}
        </ac-button>
      </div>
    </div>
  </ac-modal>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import Cookies from 'js-cookie'
import AcModal from '@ui-kit/components/AcModal.vue'
import AcButton from '@ui-kit/components/AcButton.vue'
import i18n from '@web/plugins/i18n'
import usePushSubscribe from '@web/composition/usePushSubscribe'
import { isEmbedded } from '@web/composition/useEmbedMode'

const ANSWER_KEY = 'acProject_push_answer'
const PROMPT_DELAY_MS = 10000

const isOpen = ref(false)

const title = i18n.global.t('OneSignal.slidedown.actionTitle')
const text = i18n.global.t('OneSignal.slidedown.actionText')

const {
  isOneSignalEnable,
  subscribed,
  checkSubscribed,
  subscribe,
  unsubscribe
} = usePushSubscribe()

function hasAnswer (): boolean {
  return Boolean(Cookies.get(ANSWER_KEY) ?? localStorage.getItem(ANSWER_KEY))
}

function setAnswer (value: string): void {
  Cookies.set(ANSWER_KEY, value, { sameSite: 'Strict', expires: 7 })
  localStorage.setItem(ANSWER_KEY, value)
}

onMounted(async () => {
  // Во встроенном режиме (MOB-534) браузерные пуши не работают, а свои
  // приложение запрашивает само — модалка была бы лишней и мешала контенту.
  if (isEmbedded()) return
  if (isOneSignalEnable.value && !hasAnswer()) {
    await checkSubscribed()
    setTimeout(() => {
      isOpen.value = !subscribed.value
    }, PROMPT_DELAY_MS)
  }
})

function declinePush (): void {
  isOpen.value = false
  unsubscribe()
  setAnswer('no')
}

function enablePush (): void {
  isOpen.value = false
  subscribe()
  setAnswer('yes')
}
</script>

<style lang="scss" scoped>
.asPushPrompt {
  font-family: Inter, sans-serif;
  text-align: center;
  color: var(--ac-color-black);
  padding: 30px 16px;
  background: var(--ac-color-white);
  border-radius: 12px;
  max-width: 360px;
  margin: 0 auto;

  &_title {
    display: block;
    margin-bottom: 15px;
    font-weight: 500;
    font-size: 18px;
    line-height: 24px;
  }

  &_text {
    display: block;
    font-size: 13px;
    line-height: 16px;
  }

  &_actions {
    display: flex;
    flex-flow: column nowrap;
    justify-content: center;
    margin: 25px auto 0;
    width: 100%;
  }

  &_btn {
    &.-decline {
      margin-top: 8px;
      background: var(--ac-color-background);
      color: var(--ac-color-black);
    }
  }
}
</style>
